import { ref } from 'vue';
import { WorkstreamDefinitionOperationConfigGridRow } from '@/pages/OperationsAdministration/types/WorkstreamDefinitionOperationConfigGridRow';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import { fetchWorkstreamDefinitionOperationConfigs } from '@/pages/OperationsAdministration/services/fetchWorkstreamDefinitionOperationConfigs';
import { tfWorkstreamConfigsToRow } from '@/pages/OperationsAdministration/composables/transformations/tfWorkstreamConfigsToRow';
import { ErrorDetail, getErrorDetail } from '@/pages/OperationsAdministration/types/ErrorDetail';
import { tfRowToWorkstreamConfigPatch } from '@/pages/OperationsAdministration/composables/transformations/tfRowToWorkstreamConfigPatch';
import { patchWorkstreamDefinitionOperationConfig } from '@/pages/OperationsAdministration/services/patchWorkstreamDefinitionOperationConfig';
import { useActiveWsDefOperationConfigRefDataStore } from '@/stores/activeWsDefOperationConfigRefDataStore';

export function useWorkstreamDefinitionOperationConfigData() {
    const logger = useLogger('useWorkstreamDefinitionOperationConfigData');
    const { refreshActiveWsDefOperationConfigRefDataStore } = useActiveWsDefOperationConfigRefDataStore();

    const data = ref<WorkstreamDefinitionOperationConfigGridRow[]>([]);
    const isLoading = ref(false);
    const errorDetail = ref<ErrorDetail>(null);

    const fetchData = async () => {
        isLoading.value = true;
        errorDetail.value = null;

        try {
            const response = await fetchWorkstreamDefinitionOperationConfigs();

            data.value = tfWorkstreamConfigsToRow(response);
        } catch (err) {
            const detail = getErrorDetail(err);
            errorDetail.value = detail;
            logger.error('Failed to fetch workstream definition config data', { error: detail });
        } finally {
            isLoading.value = false;
        }
    };

    const updateData = async (changes: WorkstreamDefinitionOperationConfigGridRow[]) => {
        if (changes.length === 0) {
            return true;
        }

        try {
            isLoading.value = true;
            errorDetail.value = null;

            const patches = changes.map((r) =>
                patchWorkstreamDefinitionOperationConfig(r.workstreamDefinitionId, tfRowToWorkstreamConfigPatch(r))
            );
            await Promise.all(patches);
            await refreshActiveWsDefOperationConfigRefDataStore();
        } catch (err) {
            errorDetail.value = getErrorDetail(err);
            logger.error('Failed to patch workstream definition config data', { error: err });
        } finally {
            isLoading.value = false;
        }

        await fetchData();
        return errorDetail?.value === null;
    };

    fetchData();

    return {
        data,
        isLoading,
        errorDetail,
        fetchData,
        updateData
    };
}